import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "@/components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/WorkSchedules";
import {
  setStatusWorkSchedules,
  getShowWorkSchedules,
  deleteWorkSchedulesData,
} from "@/Api/workSchedules";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { WEEKDAY_LABELS, WEEKDAY_LABELS_SHORT } from "@/utils/weekdays";
import { WorkScheduleDayProps } from "@/types/workSchedule";
import { calculateWeeklyMinutes, formatMinutesAsHours } from "@/utils/workScheduleHours";

const formatTime = (value?: string | null) => (value ? value.slice(0, 5) : "-");

const sortedDays = (days?: WorkScheduleDayProps[] | null) =>
  (days ?? []).slice().sort((a, b) => a.weekday - b.weekday);

const formatDaysSummary = (days?: WorkScheduleDayProps[] | null) => {
  const list = sortedDays(days);

  if (list.length < 1) return "-";

  return list
    .map((day) => `${WEEKDAY_LABELS_SHORT[day.weekday]} ${formatTime(day.start_time)}-${formatTime(day.end_time)}`)
    .join(", ");
};

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Unidade</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Horários</TableHead>
            <TableHead>Total Semanal</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item, index) => {
            return (
              <TableRow key={item?.id}>
                <TableCell className="truncate max-w-80">
                  {item?.unity?.name || "-"}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>
                <TableCell className="truncate max-w-96">
                  {formatDaysSummary(item?.days)}
                </TableCell>
                <TableCell>
                  {formatMinutesAsHours(calculateWeeklyMinutes(item?.days ?? []))}
                </TableCell>
                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={!!item?.is_active}
                    params={{
                      func: setStatusWorkSchedules,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: !values?.is_active,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("edit-work-schedule")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-work-schedule")}>
                      <DeleteBtn
                        params={{
                          func: deleteWorkSchedulesData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>
                    <If condition={hasPermission("show-work-schedule-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Horário de Trabalho" }}
                        params={{
                          func: getShowWorkSchedules,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => (
                          <div className="w-full h-auto">
                            <div className="flex flex-col gap-3">
                              <div className="flex gap-6">
                                <div>
                                  <span className="text-sm text-muted-foreground">Nome</span>
                                  <p className="font-medium">{responseApi?.name}</p>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">Tolerância</span>
                                  <p className="font-medium">{responseApi?.tolerance_minutes} min</p>
                                </div>
                                <div>
                                  <span className="text-sm text-muted-foreground">Total Semanal</span>
                                  <p className="font-medium">
                                    {formatMinutesAsHours(calculateWeeklyMinutes(responseApi?.days ?? []))}
                                  </p>
                                </div>
                              </div>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Dia</TableHead>
                                    <TableHead>Entrada</TableHead>
                                    <TableHead>Intervalo</TableHead>
                                    <TableHead>Saída</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {sortedDays(responseApi?.days).map((day) => (
                                    <TableRow key={day.weekday}>
                                      <TableCell>{WEEKDAY_LABELS[day.weekday]}</TableCell>
                                      <TableCell>{formatTime(day.start_time)}</TableCell>
                                      <TableCell>
                                        {day.break_duration_minutes ? `${day.break_duration_minutes} min` : "-"}
                                      </TableCell>
                                      <TableCell>{formatTime(day.end_time)}</TableCell>
                                    </TableRow>
                                  ))}
                                  {sortedDays(responseApi?.days).length < 1 && (
                                    <TableRow>
                                      <TableCell colSpan={4}>Nenhum dia configurado.</TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                        loader={<CreateLoaderInput lines={5} />}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={6}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
